"use client";

// src/ui/components/calculator/history-strip.tsx
//
// "Zuletzt berechnet" — a horizontally-scrollable strip of up to 6 recent
// calculations under the calculator card (handoff 02 §1.11). Each chip restores
// that calculation into the live hook on tap.
//
// History is read from localStorage via useSyncExternalStore so the component is
// SSR-safe (server snapshot is empty → no hydration mismatch) and reacts to writes
// without setState-in-effect: it re-snapshots on the cross-tab `storage` event and
// on a same-tab `bb-pr-history` CustomEvent that the calculator dispatches after a
// settled calculation is persisted (see HISTORY_EVENT).

import { useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import {
	loadHistory,
	BB_PR_HISTORY,
	HISTORY_EVENT,
	type HistoryEntry,
} from "@/lib/calculator/calculator-storage";

export interface HistoryStripRestore {
	peptideSlug: string;
	peptideAmount: number;
	bacWater: number;
	dose: number;
	mode: string;
}

// ── Cached external store ────────────────────────────────────────────────────
// useSyncExternalStore demands a referentially-stable snapshot, but loadHistory()
// returns a fresh array each call. We cache the last parsed array and only re-read
// when a change is signalled, so getSnapshot returns a stable reference between
// changes (no render loop).
let cache: HistoryEntry[] = [];
let dirty = true;

const EMPTY: HistoryEntry[] = [];

function subscribe(onChange: () => void): () => void {
	function handleStorage(e: StorageEvent) {
		if (e.key === BB_PR_HISTORY) {
			dirty = true;
			onChange();
		}
	}
	function handleLocal() {
		dirty = true;
		onChange();
	}
	window.addEventListener("storage", handleStorage);
	window.addEventListener(HISTORY_EVENT, handleLocal);
	return () => {
		window.removeEventListener("storage", handleStorage);
		window.removeEventListener(HISTORY_EVENT, handleLocal);
	};
}

function getSnapshot(): HistoryEntry[] {
	if (dirty) {
		cache = loadHistory();
		dirty = false;
	}
	return cache;
}

// Server render (and the first client snapshot before hydration) sees an empty
// list so SSR and the client initial paint agree.
function getServerSnapshot(): HistoryEntry[] {
	return EMPTY;
}

interface HistoryStripProps {
	/** Restores a chip's calculation into the live calculator state. */
	onRestore: (entry: HistoryStripRestore) => void;
}

export function HistoryStrip({ onRestore }: HistoryStripProps) {
	const t = useTranslations("peptideCalculator");
	const items = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

	if (items.length === 0) return null;

	// No outer margin here — the calculator places this strip inside the input
	// grid/flex and owns the spacing (mobile: flex gap; desktop: lg:mt margin),
	// so an internal top margin would double the gap on mobile.
	return (
		<div className="space-y-2">
			<span className="text-xs font-semibold uppercase tracking-[0.13em] text-muted-foreground">
				{t("history.label")}
			</span>
			<div className="-mx-1 flex snap-x gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:thin]" role="list">
				{items.map((h) => (
					<button
						key={h.sig}
						type="button"
						role="listitem"
						title={t("history.restore")}
						aria-label={t("history.restoreAria", { name: h.peptideName, iu: h.iu })}
						onClick={() =>
							onRestore({
								peptideSlug: h.peptideSlug,
								peptideAmount: h.peptideAmount,
								bacWater: h.bacWater,
								dose: h.dose,
								mode: h.mode,
							})
						}
						className="hover:border-primary/50 hover:bg-card/80 flex min-h-11 shrink-0 snap-start flex-col items-start gap-0.5 rounded-xl border border-border bg-card px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
					>
						<span className="text-sm font-semibold text-foreground">{h.peptideName}</span>
						<span className="flex items-center gap-1.5 whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
							<span>
								{h.peptideAmount} mg · {String(h.bacWater).replace(".", ",")} ml · {h.doseDisplay}
							</span>
							<span aria-hidden className="text-border">
								·
							</span>
							<span className="font-semibold text-foreground">{h.iu} IU</span>
						</span>
					</button>
				))}
			</div>
		</div>
	);
}
